import { useState, useRef } from 'react';

interface ComparisonViewProps {
  imageA: string;
  imageB: string;
  promptA: string;
  promptB: string;
  style: string;
  aspectRatio: string;
  quality: string;
  onPickA: () => void;
  onPickB: () => void;
  onClose: () => void;
  darkMode: boolean;
}

export default function ComparisonView({
  imageA,
  imageB,
  promptA,
  promptB,
  style,
  aspectRatio,
  quality,
  onPickA,
  onPickB,
  onClose,
  darkMode,
}: ComparisonViewProps) {
  const [sliderMode, setSliderMode] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const getAspectClass = () => {
    if (aspectRatio === '16:9') return 'aspect-video';
    if (aspectRatio === '9:16') return 'aspect-[9/16]';
    if (aspectRatio === '4:3') return 'aspect-[4/3]';
    return 'aspect-square';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setSliderPos(Math.max(5, Math.min(95, pct)));
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.touches[0].clientX - rect.left) / rect.width) * 100;
    setSliderPos(Math.max(5, Math.min(95, pct)));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div
        className={`w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
        style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
              <i className="ri-layout-column-line text-white text-sm"></i>
            </div>
            <div>
              <h3 className={`font-bold text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Compare Variations
              </h3>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {style} · {aspectRatio} · {quality}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mode toggle */}
            <div className={`flex items-center gap-1 p-1 rounded-lg text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <button
                onClick={() => setSliderMode(false)}
                className={`px-3 py-1.5 rounded-md font-medium transition-all ${!sliderMode
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                Side by Side
              </button>
              <button
                onClick={() => setSliderMode(true)}
                className={`px-3 py-1.5 rounded-md font-medium transition-all ${sliderMode
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                Slider
              </button>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Images */}
        <div className="flex-1 overflow-y-auto p-6">
          {sliderMode ? (
            /* Slider mode */
            <div
              ref={containerRef}
              className={`relative w-full ${getAspectClass()} overflow-hidden rounded-xl cursor-col-resize select-none`}
              onMouseMove={handleMouseMove}
              onMouseUp={() => setDragging(false)}
              onMouseLeave={() => setDragging(false)}
              onTouchMove={handleTouchMove}
              onTouchEnd={() => setDragging(false)}
            >
              {/* Image B (right / full) */}
              <img src={imageB} alt="Variation B" className="absolute inset-0 w-full h-full object-cover" />
              {/* Image A (left / clipped) */}
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
                <img src={imageA} alt="Variation A" className="absolute inset-0 w-full h-full object-cover" style={{ width: `${10000 / sliderPos}%`, maxWidth: 'none' }} />
              </div>
              {/* Slider handle */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white z-10 cursor-col-resize"
                style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
                onMouseDown={() => setDragging(true)}
                onTouchStart={() => setDragging(true)}
              >
                <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                  <i className="ri-drag-move-2-line text-gray-700 text-sm"></i>
                </div>
              </div>
              {/* Labels */}
              <span className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 text-white text-xs font-bold rounded-lg backdrop-blur-sm">A</span>
              <span className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 text-white text-xs font-bold rounded-lg backdrop-blur-sm">B</span>
            </div>
          ) : (
            /* Side by side mode */
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'A', image: imageA, prompt: promptA, onPick: onPickA },
                { label: 'B', image: imageB, prompt: promptB, onPick: onPickB },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`rounded-xl overflow-hidden border-2 transition-all group ${darkMode ? 'border-gray-700 hover:border-purple-500' : 'border-gray-200 hover:border-purple-400'}`}
                >
                  <div className={`relative ${getAspectClass()} overflow-hidden`}>
                    <img src={item.image} alt={`Variation ${item.label}`} className="w-full h-full object-cover" />
                    {/* Badge */}
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 text-white text-xs font-bold rounded-lg backdrop-blur-sm">
                      Variation {item.label}
                    </span>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        onClick={item.onPick}
                        className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:opacity-90 transition-opacity text-sm"
                      >
                        <i className="ri-check-double-line mr-2"></i>Use This One
                      </button>
                    </div>
                  </div>
                  <div className={`p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    <p className={`text-xs mb-3 line-clamp-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {item.prompt}
                    </p>
                    <button
                      onClick={item.onPick}
                      className="w-full py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity"
                    >
                      <i className="ri-check-line mr-1.5"></i>Pick Variation {item.label}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Prompt diff */}
          {promptA !== promptB && (
            <div className={`mt-4 p-4 rounded-xl text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <i className="ri-information-line mr-1.5"></i>Prompts used
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                  <span className="font-bold text-purple-500 mr-1">A:</span>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{promptA}</span>
                </div>
                <div className={`p-2.5 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                  <span className="font-bold text-pink-500 mr-1">B:</span>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{promptB}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex items-center gap-2 flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <i className={`ri-lightbulb-line text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Tip: Use Slider mode to see subtle differences pixel by pixel. Drag the handle left and right.
          </span>
          <button onClick={onClose} className={`ml-auto px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
            Discard & Close
          </button>
        </div>
      </div>
    </div>
  );
}
